import React from "react";
import ArrowRightUpLine from "../../Icons/ArrowRightUpLine";
import { Link } from "react-router-dom";
import ArrowRightUpLineDark from "../../Icons/ArrowRightUpLineDark";

// Utility function to merge classes (simplified version of cn)
const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// CTAButton variants using the rainbow pattern
const ctaButtonVariants = (variant = "default", size = "default") => {
  const baseClasses = cn(
    "relative cursor-pointer group transition-all animate-rainbow",
    "inline-flex items-center justify-center gap-2 shrink-0",
    "rounded-sm outline-none focus-visible:ring-[3px]",
    "text-sm font-medium whitespace-nowrap font-['Montserrat'] uppercase",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0"
  );

  const variants = {
    default: "border-0 bg-[linear-gradient(#5A3232,#5A3232),linear-gradient(#5A3232_50%,rgba(90,50,50,0.6)_80%,rgba(90,50,50,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] text-white [background-clip:padding-box,border-box,border-box] [background-origin:border-box] [border:calc(0.125rem)_solid_transparent] before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:[filter:blur(0.75rem)]",
    white: "bg-white text-black border border-gray-300",
    rainbow: "border-0 bg-[linear-gradient(#5A3232,#5A3232),linear-gradient(#5A3232_50%,rgba(90,50,50,0.6)_80%,rgba(90,50,50,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] text-white [background-clip:padding-box,border-box,border-box] [background-origin:border-box] [border:calc(0.125rem)_solid_transparent] before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:[filter:blur(0.75rem)]",
    whiteRainbow: "border-0 bg-[linear-gradient(#ffffff,#ffffff),linear-gradient(#ffffff_50%,rgba(255,255,255,0.6)_80%,rgba(255,255,255,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] text-black [background-clip:padding-box,border-box,border-box] [background-origin:border-box] [border:calc(0.125rem)_solid_transparent] before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:[filter:blur(0.75rem)]",
    outline: "border border-input border-b-transparent bg-[linear-gradient(#ffffff,#ffffff),linear-gradient(#ffffff_50%,rgba(18,18,19,0.6)_80%,rgba(18,18,19,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] text-black [background-clip:padding-box,border-box,border-box] [background-origin:border-box] before:absolute before:bottom-[-20%] before:left-1/2 before:z-0 before:h-1/5 before:w-3/5 before:-translate-x-1/2 before:animate-rainbow before:bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] before:[filter:blur(0.75rem)] dark:bg-[linear-gradient(#0a0a0a,#0a0a0a),linear-gradient(#0a0a0a_50%,rgba(255,255,255,0.6)_80%,rgba(0,0,0,0)),linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))]"
  };

  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 px-3 text-xs",
    lg: "h-11 px-8",
    icon: "size-9"
  };

  return cn(baseClasses, variants[variant], sizes[size]);
};

const CTAButton = React.forwardRef(({ 
  text = "Placeholder", 
  url = "#", 
  isWhiteButton = false, 
  isRainbow = false,
  variant,
  size = "default",
  className,
  ...props 
}, ref) => {
  // Determine the variant based on props
  const buttonVariant = variant || (isRainbow ? "rainbow" : isWhiteButton ? "white" : "default");
  
  return (
    <Link
      to={url}
      className={cn(ctaButtonVariants(buttonVariant, size), className)}
      ref={ref}
      {...props}
    >
      <span className="relative z-10">
        {text}
      </span>
      <div className="w-4 h-4 relative overflow-hidden z-10">
        {!isWhiteButton && buttonVariant !== "outline" && buttonVariant !== "whiteRainbow" ? (
          <ArrowRightUpLine className="text-white" />
        ) : (
          <ArrowRightUpLineDark className="text-black brightness-0" style={{ filter: 'brightness(0)' }} />
        )}
      </div>
    </Link>
  );
});

CTAButton.displayName = "CTAButton";

export default CTAButton;
